import React, { useContext, useEffect, useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Toolbar,
  Typography,
  ListItemButton,
  CircularProgress,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate } from 'react-router-dom';
import FormsBottomNavbar from '../Buy Module/CustomNav'; // adjust path if needed
import { AuthContext } from '../../AuthContext/AuthContext';
import axios from 'axios';
import { BASE_URL } from '../../Api/ApiUrls';

const API_BASE = 'https://landnest.net:7000/api';

const JvJdInboxList = () => {
  const navigate = useNavigate();
  const { userId } = useContext(AuthContext);

  const [messages, setMessages] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [users, setUsers] = useState({});
  const [properties, setProperties] = useState({});
  const [loading, setLoading] = useState(true);

  const colors = {
    primary: '#1a1a1a',
    background: '#efe7dd',
    cardBg: '#ffffff',
    muted: '#666',
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // ── 1. JV/JD properties ─────────────────────────────────────────────
        // Prefer dedicated JV/JD endpoint; fall back to main property list if needed
        let propertiesArray = [];
        try {
          const jvjdRes = await axios.get(
            `${API_BASE}/properties/jvjd/jvjd/non-admin/?limit=500`
          );
          propertiesArray = Array.isArray(jvjdRes.data?.data)
            ? jvjdRes.data.data
            : Array.isArray(jvjdRes.data)
              ? jvjdRes.data
              : [];
        } catch {
          // Fallback: main property API (filter client-side if your API supports type)
          const propRes = await axios.get(`${BASE_URL}/property/`);
          propertiesArray = propRes.data?.data || propRes.data || [];
        }

        const propertiesData = propertiesArray.reduce((acc, property) => {
          const pid =
            property.id ??
            property.property_id ??
            property.req_id;
          if (pid != null) {
            acc[String(pid)] = {
              ...property,
              property_id: pid,
              property_name:
                property.property_name ||
                property.propertyType ||
                property.property_type ||
                property.category_name ||
                `Property ${pid}`,
              user_id: property.userId ?? property.user_id,
            };
          }
          return acc;
        }, {});
        setProperties(propertiesData);

        // ── 2. Messages ─────────────────────────────────────────────────────
        const messagesResponse = await axios.get(`${BASE_URL}/chat-messages/`);
        const messagesData = (messagesResponse.data || []).filter(
          (msg) => msg.property_id != null
        );

        // ── 3. Users ────────────────────────────────────────────────────────
        const usersResponse = await axios.get(`${BASE_URL}/users/`);
        const usersArray = usersResponse.data?.data || usersResponse.data || [];
        const usersData = usersArray.reduce((acc, user) => {
          acc[user.user_id] = user;
          return acc;
        }, {});
        setUsers(usersData);

        // ── 4. Only messages involving current user ─────────────────────────
        const userMessages = messagesData.filter(
          (msg) =>
            String(msg.user_id) === String(userId) ||
            String(msg.receiver) === String(userId)
        );

        // Optional: keep only chats tied to known JV/JD properties
        // (comment out the next filter if you want all chats regardless)
        const jvjdPropertyIds = new Set(Object.keys(propertiesData));
        const jvjdUserMessages = userMessages.filter((msg) =>
          jvjdPropertyIds.has(String(msg.property_id))
        );

        // Latest message per property
        const latestMessagesMap = new Map();
        jvjdUserMessages.forEach((msg) => {
          const key = String(msg.property_id);
          if (!latestMessagesMap.has(key)) {
            latestMessagesMap.set(key, msg);
          }
        });

        const latestMessages = Array.from(latestMessagesMap.values());
        setMessages(latestMessages);
        setFilteredMessages(latestMessages);
      } catch (error) {
        console.error('Failed to fetch JV/JD inbox data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) fetchData();
  }, [userId]);

  // Search filter
  useEffect(() => {
    if (loading) return;
    const lowerSearch = searchText.toLowerCase();
    const filtered = messages.filter((msg) => {
      const prop = properties[String(msg.property_id)] || {};
      const name = (prop.property_name || '').toLowerCase();
      return (
        (msg.message || '').toLowerCase().includes(lowerSearch) ||
        name.includes(lowerSearch) ||
        String(msg.property_id || '').includes(lowerSearch)
      );
    });
    setFilteredMessages(filtered);
  }, [searchText, messages, properties, loading]);

  const getOtherUser = (message) => {
    const otherUserId =
      String(message.user_id) === String(userId)
        ? message.receiver
        : message.user_id;
    return users[otherUserId] || {};
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          backgroundColor: colors.background,
        }}
      >
        <CircularProgress sx={{ color: '#000' }} />
        <Typography fontWeight={600}>Loading JV/JD Inbox...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 800,
        mx: 'auto',
        minHeight: '100vh',
        bgcolor: 'rgb(239, 231, 221)',
        borderRadius: { xs: 0, sm: 3 },
        boxShadow: 3,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <AppBar position="static" elevation={0} sx={{ bgcolor: 'black', color: 'white' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <IconButton
            edge="start"
            size="small"
            onClick={() => navigate(-1)}
            sx={{ color: 'white' }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            JV/JD Inbox ({filteredMessages.length})
          </Typography>
          <IconButton edge="end" size="small" sx={{ color: 'white' }} />
        </Toolbar>
      </AppBar>

      <Paper
        sx={{
          m: 2,
          px: 2,
          py: 0.5,
          display: 'flex',
          alignItems: 'center',
          borderRadius: 5,
          backgroundColor: '#f0f0f0',
          border: '1px solid black',
        }}
      >
        <Search sx={{ color: 'gray', mr: 1 }} />
        <InputBase
          placeholder="Search JV/JD chats"
          fullWidth
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </Paper>

      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 1, pb: 7 }}>
        <List>
          {filteredMessages.length > 0 ? (
            filteredMessages.map((msg, index) => {
              const property = properties[String(msg.property_id)] || {};
              const otherUser = getOtherUser(msg);
              const isPropertyOwner =
                String(property.user_id) === String(userId);

              return (
                <React.Fragment key={`${msg.property_id}-${index}`}>
                  <ListItemButton
                    alignItems="flex-start"
                    onClick={() =>
                      navigate(
                        `/chat/${msg.property_id}/${otherUser.user_id || msg.receiver || msg.user_id}`
                      )
                    }
                  >
                    <ListItemAvatar>
                      <Avatar src={otherUser.profile}>
                        {otherUser.first_name?.[0] || 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 1,
                          }}
                        >
                          <Typography fontWeight={500} noWrap>
                            {property.property_name ||
                              `Property ${msg.property_id}`}
                            {isPropertyOwner && ' (Your Property)'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {msg.created_at
                              ? new Date(msg.created_at).toLocaleDateString()
                              : ''}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" noWrap>
                          {msg.message}
                        </Typography>
                      }
                    />
                  </ListItemButton>

                  {index !== filteredMessages.length - 1 && (
                    <Divider variant="inset" component="li" />
                  )}
                </React.Fragment>
              );
            })
          ) : (
            <ListItem>
              <ListItemText
                primary="No JV/JD messages found"
                secondary="You don't have any JV/JD conversations yet"
                sx={{ textAlign: 'center' }}
              />
            </ListItem>
          )}
        </List>
      </Box>

      <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0 }}>
        <FormsBottomNavbar />
      </Box>
    </Box>
  );
};

export default JvJdInboxList;